/* Strict Content Release JSON schemas.
 *
 * Authority: 06-content-pipeline.md §2–§3. This is the ONE place in the system
 * where unknown fields are a hard error rather than tolerantly ignored
 * (05-api-contracts.md §1.1): content is where silent drift is unacceptable.
 * Every object below therefore sets additionalProperties:false.
 *
 * Drift corrections already applied here, so the validator needs no special cases:
 *   DR-08  weight/bonus_weight are REALS (>0, >=0), not 06's integer 1–5 grammar
 *   DR-09  rubric criteria score 0–3 (not 0–4)
 *   DR-10  kebab-case ids
 *   DR-12  exactly four quiz item kinds; no multi-select, no standalone exact
 *   DR-18  review object carries NO result field — the server computes score/passed
 *   DR-21  items[].topic_id optional, required on Camp/Summit assessments (V9)
 *   DR-25  manifest.min_server_schema_version is part of the schema
 */
import { Type } from '@sinclair/typebox';
import { ID_PATTERNS } from '../ids.js';
const p = (re) => re.source;
/** Closed object: additionalProperties:false, matching the strict-content rule. */
const Obj = (props, opts = {}) => Type.Object(props, { additionalProperties: false, ...opts });
// ---------------------------------------------------------------------------
// Provenance (06 §2.2). Required on every Unit; V16 enforces the notes rule.
// ---------------------------------------------------------------------------
export const ProvenanceSourceSchema = Obj({
    kind: Type.Union([Type.Literal('course'), Type.Literal('doc'), Type.Literal('generated')]),
    ref: Type.String({ minLength: 1 }),
    note: Type.Optional(Type.String()),
});
export const ProvenanceSchema = Obj({
    regime: Type.Union([Type.Literal('A'), Type.Literal('B')]),
    sources: Type.Array(ProvenanceSourceSchema, { minItems: 1 }),
    verify_by: Type.Optional(Type.String({ format: 'date' })),
});
// ---------------------------------------------------------------------------
// Code exercise fields (06 §2.5 kind-specific / 04 §2.5.1).
// `solution_code` ships for Factory self-test (G10) and is stripped by the
// server before ANY client response (V17 / C44).
// ---------------------------------------------------------------------------
export const CodePythonFields = {
    starter_code: Type.String(),
    checks_py: Type.String({ minLength: 1 }),
    solution_code: Type.String(),
};
export const CodeSqlFields = {
    seed_sql: Type.String({ minLength: 1 }),
    expected_rows: Type.Array(Type.Array(Type.Unknown())),
    order_sensitive: Type.Boolean(),
};
/** A drill Card may carry an exercise, making it a code Card in rotation. */
export const CardExerciseSchema = Type.Union([
    Obj({ type: Type.Literal('python'), ...CodePythonFields }),
    Obj({ type: Type.Literal('sql'), ...CodeSqlFields }),
    Obj({
        type: Type.Literal('reading'),
        code_md: Type.String(),
        answer_mode: Type.Literal('exact'),
        accepted_answers: Type.Array(Type.String(), { minItems: 1 }),
    }),
]);
// ---------------------------------------------------------------------------
// Common Unit envelope (06 §2.1). DR-17: est_minutes + body_md win over 04's
// estimated_minutes/body.
// ---------------------------------------------------------------------------
const envelope = {
    id: Type.String({ pattern: p(ID_PATTERNS.unit) }),
    topic_id: Type.String({ pattern: p(ID_PATTERNS.topic) }),
    title: Type.String({ minLength: 1, maxLength: 80 }),
    version: Type.Integer({ minimum: 1 }),
    provenance: ProvenanceSchema,
    est_minutes: Type.Integer({ minimum: 1, maximum: 90 }),
    body_md: Type.String({ minLength: 1 }),
};
// ---------------------------------------------------------------------------
// lesson (06 §2.3)
// ---------------------------------------------------------------------------
export const LessonUnitSchema = Obj({
    ...envelope,
    type: Type.Literal('lesson'),
    sections_md: Type.Array(Obj({
        id: Type.String({ minLength: 1, maxLength: 64 }),
        heading: Type.String({ minLength: 1, maxLength: 60 }),
        md: Type.String({ minLength: 1 }),
    }), { minItems: 1, maxItems: 8 }),
});
// ---------------------------------------------------------------------------
// drill_deck (06 §2.4). `hint` is required-but-nullable, per the schema table.
// ---------------------------------------------------------------------------
export const DrillDeckUnitSchema = Obj({
    ...envelope,
    type: Type.Literal('drill_deck'),
    cards: Type.Array(Obj({
        id: Type.String({ pattern: p(ID_PATTERNS.card) }),
        front_md: Type.String({ minLength: 1 }),
        back_md: Type.String({ minLength: 1 }),
        hint: Type.Union([Type.String(), Type.Null()]),
        tags: Type.Optional(Type.Array(Type.String({ pattern: '^[a-z0-9][a-z0-9-]*$' }))),
        exercise: Type.Optional(CardExerciseSchema),
    }), { minItems: 1, maxItems: 40 }),
});
// ---------------------------------------------------------------------------
// quiz (06 §2.5). Confidence is captured at runtime, never authored.
// ---------------------------------------------------------------------------
const quizItemBase = {
    id: Type.String({ pattern: p(ID_PATTERNS.quizItem) }),
    topic_id: Type.Optional(Type.String({ pattern: p(ID_PATTERNS.topic) })),
    stem_md: Type.String({ minLength: 1 }),
    explanation_md: Type.String({ minLength: 1 }),
    difficulty: Type.Integer({ minimum: 1, maximum: 5 }),
};
export const QuizItemSchema = Type.Union([
    Obj({
        ...quizItemBase,
        kind: Type.Literal('mcq'),
        options: Type.Array(Type.String({ minLength: 1 }), { minItems: 4, maxItems: 4 }),
        correct_index: Type.Integer({ minimum: 0, maximum: 3 }),
    }),
    Obj({ ...quizItemBase, kind: Type.Literal('code_python'), ...CodePythonFields }),
    Obj({ ...quizItemBase, kind: Type.Literal('code_sql'), ...CodeSqlFields }),
    Obj({
        ...quizItemBase,
        kind: Type.Literal('code_reading'),
        code_md: Type.String({ minLength: 1 }),
        answer_mode: Type.Literal('exact'),
        accepted_answers: Type.Array(Type.String(), { minItems: 1 }),
    }),
    Obj({
        ...quizItemBase,
        kind: Type.Literal('code_reading'),
        code_md: Type.String({ minLength: 1 }),
        answer_mode: Type.Literal('choice'),
        options: Type.Array(Type.String({ minLength: 1 }), { minItems: 4, maxItems: 4 }),
        correct_index: Type.Integer({ minimum: 0, maximum: 3 }),
    }),
]);
export const QuizUnitSchema = Obj({
    ...envelope,
    type: Type.Literal('quiz'),
    items: Type.Array(QuizItemSchema, { minItems: 3, maxItems: 30 }),
});
// ---------------------------------------------------------------------------
// Shared deliverable + rubric shapes (06 §2.6/§2.7).
// ---------------------------------------------------------------------------
export const DeliverableSpecSchema = Obj({
    type: Type.Union([Type.Literal('text'), Type.Literal('link'), Type.Literal('checklist')]),
    prompt: Type.String({ minLength: 1 }),
    items: Type.Optional(Type.Array(Type.String({ minLength: 1 }))),
});
export const RubricEntrySchema = Obj({
    id: Type.String({ minLength: 1, maxLength: 64 }),
    criterion: Type.String({ minLength: 1 }),
    weight: Type.Integer({ minimum: 1, maximum: 5 }),
    pass_md: Type.String({ minLength: 1 }),
});
/* Binds a Unit to the Factory's topic ledger (CR-020). Coverage of the
 * destination profile is DERIVED from what shipped Units declare here, so it
 * can never drift into a hand-maintained fiction. Required on `build`; optional
 * on `lab`, so the permanent archive of Units authored before CR-020 stays
 * valid. Topic item id, e.g. `T5.4.3` — subsection number plus bullet ordinal. */
export const CoversSchema = Obj({
    topic_items: Type.Array(Type.String({ pattern: '^T\\d+\\.\\d+\\.\\d+$' }), { minItems: 1 }),
    /** Factory index source ids — the academic material this task is grounded in. */
    sources: Type.Array(Type.String({ minLength: 1 })),
});
// ---------------------------------------------------------------------------
// mission (06 §2.6). The final step MUST be `submit` (02 §2.6 / A22) — a
// referential rule, checked in the validator rather than expressible in JSON Schema.
// ---------------------------------------------------------------------------
export const MissionStepSchema = Obj({
    id: Type.String({ pattern: p(ID_PATTERNS.missionStep) }),
    title: Type.String({ minLength: 1, maxLength: 60 }),
    kind: Type.Union([
        Type.Literal('investigate'),
        Type.Literal('build'),
        Type.Literal('read'),
        Type.Literal('submit'),
    ]),
    instructions_md: Type.String({ minLength: 1 }),
    deliverable: DeliverableSpecSchema,
    required_units: Type.Array(Type.String({ pattern: p(ID_PATTERNS.unit) })),
    lab_unit_id: Type.Optional(Type.String({ pattern: p(ID_PATTERNS.unit) })),
    rubric: Type.Optional(Type.Array(RubricEntrySchema, { minItems: 2 })),
    pass_threshold: Type.Optional(Type.Number({ minimum: 0, maximum: 3 })),
    est_minutes: Type.Optional(Type.Integer({ minimum: 1, maximum: 600 })),
});
export const MissionUnitSchema = Obj({
    ...envelope,
    type: Type.Literal('mission'),
    narrative_md: Type.String({ minLength: 1 }),
    steps: Type.Array(MissionStepSchema, { minItems: 4, maxItems: 7 }),
});
// ---------------------------------------------------------------------------
// lab (06 §2.7). `review` is required-but-nullable and is ALWAYS null at
// authoring time; it carries no result field (DR-18).
// ---------------------------------------------------------------------------
export const EmbeddedReviewSchema = Obj({
    review_id: Type.String({ pattern: p(ID_PATTERNS.uuidV4) }),
    submission_id: Type.String({ pattern: p(ID_PATTERNS.uuidV4) }),
    reviewed_at: Type.String({ format: 'date-time' }),
    criterion_scores: Type.Record(Type.String(), Type.Integer({ minimum: 0, maximum: 3 })),
    feedback_md: Type.String({ minLength: 1 }),
});
export const LabUnitSchema = Obj({
    ...envelope,
    type: Type.Literal('lab'),
    brief_md: Type.String({ minLength: 1 }),
    target_system: Type.String({ minLength: 1 }),
    access_mode: Type.Union([Type.Literal('read_only'), Type.Literal('guided_write')]),
    safety_notes_md: Type.Optional(Type.String({ minLength: 1 })),
    deliverable: DeliverableSpecSchema,
    rubric: Type.Array(RubricEntrySchema, { minItems: 2 }),
    pass_threshold: Type.Optional(Type.Number({ minimum: 0, maximum: 3 })),
    /** CR-020: judgement items reach the ledger through Labs. Optional so the
     *  permanent archive of pre-CR-020 Labs stays valid. */
    covers: Type.Optional(CoversSchema),
    review: Type.Union([EmbeddedReviewSchema, Type.Null()]),
});
// ---------------------------------------------------------------------------
// build (CR-020). The workbench unit: the learner writes code against a system
// under construction and real checks decide whether it works.
//
// Two things make this different from a code quiz item. First, `covers` binds
// the unit to the Factory's topic ledger, so coverage is derived from what
// shipped rather than hand-maintained. Second, `prediction` is answered BEFORE
// the first run — credit needs green checks AND a correct call, because a pass
// you did not expect is a gap, not a win (ai-profiel §6.5).
// ---------------------------------------------------------------------------
/**
 * A prediction option asserts an OBSERVABLE run outcome. There is deliberately
 * no stored correct index: correctness is decided by the actual run, so there
 * is no answer key to strip and nothing to read out of the shipped payload.
 */
export const PredictionOptionSchema = Obj({
    id: Type.String({ minLength: 1, maxLength: 32 }),
    label_md: Type.String({ minLength: 1 }),
    asserts: Obj({
        outcome: Type.Union([Type.Literal('all_pass'), Type.Literal('any_fail')]),
        /** Optional: name the single check this option is about. */
        check: Type.Optional(Type.String({ minLength: 1 })),
    }),
});
export const PredictionSchema = Obj({
    stem_md: Type.String({ minLength: 1 }),
    options: Type.Array(PredictionOptionSchema, { minItems: 2, maxItems: 4 }),
});
export const BuildUnitSchema = Obj({
    ...envelope,
    type: Type.Literal('build'),
    goal_md: Type.String({ minLength: 1 }),
    starter_code: Type.String(),
    checks_py: Type.String({ minLength: 1 }),
    /** Stripped by the server before any client response, exactly like a code item. */
    solution_code: Type.String({ minLength: 1 }),
    /** Optional SQL to create the system's tables before the learner's code runs. */
    seed_sql: Type.Optional(Type.String({ minLength: 1 })),
    prediction: PredictionSchema,
    /** A proof task closes a phase — no scaffolding, and it may be attempted first to skip. */
    proof: Type.Optional(Type.Boolean()),
    covers: CoversSchema,
    reference_note_md: Type.Optional(Type.String({ minLength: 1 })),
});
export const UnitSchema = Type.Union([
    LessonUnitSchema,
    DrillDeckUnitSchema,
    QuizUnitSchema,
    MissionUnitSchema,
    LabUnitSchema,
    BuildUnitSchema,
]);
// ---------------------------------------------------------------------------
// atlas.json (06 §3). DR-01: camp altitude_pct is an integer 1–99.
// ---------------------------------------------------------------------------
export const AtlasTopicSchema = Obj({
    id: Type.String({ pattern: p(ID_PATTERNS.topic) }),
    title: Type.String({ minLength: 1, maxLength: 60 }),
    domain: Type.String({ pattern: p(ID_PATTERNS.domain) }),
    secondary_domain: Type.Union([Type.String({ pattern: p(ID_PATTERNS.domain) }), Type.Null()]),
    regime: Type.Union([Type.Literal('A'), Type.Literal('B')]),
    weight: Type.Number({ exclusiveMinimum: 0 }),
    bonus_weight: Type.Number({ minimum: 0 }),
    prereqs: Type.Array(Type.String({ pattern: p(ID_PATTERNS.topic) })),
    retired: Type.Optional(Type.Boolean()),
});
export const AtlasCampSchema = Obj({
    id: Type.String({ minLength: 1, maxLength: 64 }),
    name: Type.String({ minLength: 1, maxLength: 40 }),
    altitude_pct: Type.Integer({ minimum: 1, maximum: 99 }),
    assessment_unit_id: Type.String({ pattern: p(ID_PATTERNS.unit) }),
});
export const AtlasExpeditionSchema = Obj({
    id: Type.String({ pattern: p(ID_PATTERNS.expedition) }),
    peak: Type.String({ minLength: 1 }),
    status: Type.Union([Type.Literal('active'), Type.Literal('planned'), Type.Literal('completed')]),
    goal_md: Type.String({ minLength: 1 }),
    weeks: Type.Integer({ minimum: 1, maximum: 60 }),
    topic_ids: Type.Array(Type.String({ pattern: p(ID_PATTERNS.topic) }), { minItems: 1 }),
    mission_unit_ids: Type.Array(Type.String({ pattern: p(ID_PATTERNS.unit) })),
    camps: Type.Array(AtlasCampSchema),
    summit_assessment_unit_id: Type.String({ pattern: p(ID_PATTERNS.unit) }),
    summit_scenario_unit_id: Type.String({ pattern: p(ID_PATTERNS.unit) }),
});
export const AtlasSchema = Obj({
    atlas_version: Type.Integer({ minimum: 1 }),
    generated_at: Type.String({ format: 'date-time' }),
    clusters: Type.Array(Obj({
        id: Type.String({ pattern: p(ID_PATTERNS.cluster) }),
        name: Type.String({ minLength: 1 }),
        domains: Type.Array(Type.String({ pattern: p(ID_PATTERNS.domain) }), { minItems: 1 }),
    }), { minItems: 5, maxItems: 5 }),
    domains: Type.Array(Obj({
        id: Type.String({ pattern: p(ID_PATTERNS.domain) }),
        name: Type.String({ minLength: 1 }),
        cluster: Type.String({ pattern: p(ID_PATTERNS.cluster) }),
    }), { minItems: 16, maxItems: 16 }),
    topics: Type.Array(AtlasTopicSchema, { minItems: 1 }),
    expeditions: Type.Array(AtlasExpeditionSchema),
});
// ---------------------------------------------------------------------------
// manifest.json (06 §4.2)
// ---------------------------------------------------------------------------
export const ManifestSchema = Obj({
    release_id: Type.String({ pattern: p(ID_PATTERNS.releaseId) }),
    created_at: Type.String({ format: 'date-time' }),
    prior_release_id: Type.Union([Type.String({ pattern: p(ID_PATTERNS.releaseId) }), Type.Null()]),
    atlas_version: Type.Integer({ minimum: 1 }),
    min_server_schema_version: Type.Integer({ minimum: 1 }),
    factory: Obj({
        operator: Type.String({ minLength: 1 }),
        tool: Type.String({ minLength: 1 }),
        model: Type.String({ minLength: 1 }),
    }),
    counts: Obj({
        topics: Type.Integer({ minimum: 0 }),
        units_total: Type.Integer({ minimum: 0 }),
        lesson: Type.Integer({ minimum: 0 }),
        drill_deck: Type.Integer({ minimum: 0 }),
        quiz: Type.Integer({ minimum: 0 }),
        mission: Type.Integer({ minimum: 0 }),
        lab: Type.Integer({ minimum: 0 }),
        /* CR-020. Optional so the permanent archive of manifests written before the
         * build unit type existed still validates on a re-check. */
        build: Type.Optional(Type.Integer({ minimum: 0 })),
        reviews: Type.Integer({ minimum: 0 }),
        assets: Type.Integer({ minimum: 0 }),
    }),
    provenance_summary: Obj({
        regime_a_units: Type.Integer({ minimum: 0 }),
        regime_b_units: Type.Integer({ minimum: 0 }),
        source_kinds: Obj({
            course: Type.Integer({ minimum: 0 }),
            doc: Type.Integer({ minimum: 0 }),
            generated: Type.Integer({ minimum: 0 }),
        }),
        verify_by_earliest: Type.Union([Type.String({ format: 'date' }), Type.Null()]),
    }),
    notes_md: Type.String(),
});
/** reviews/*.json = the review object plus its target Lab (06 §4.1). */
export const ReleaseReviewFileSchema = Obj({
    lab_unit_id: Type.String({ pattern: p(ID_PATTERNS.unit) }),
    review_id: Type.String({ pattern: p(ID_PATTERNS.uuidV4) }),
    submission_id: Type.String({ pattern: p(ID_PATTERNS.uuidV4) }),
    reviewed_at: Type.String({ format: 'date-time' }),
    criterion_scores: Type.Record(Type.String(), Type.Integer({ minimum: 0, maximum: 3 })),
    feedback_md: Type.String({ minLength: 1 }),
});
export const UNIT_SCHEMA_BY_TYPE = {
    lesson: LessonUnitSchema,
    drill_deck: DrillDeckUnitSchema,
    quiz: QuizUnitSchema,
    mission: MissionUnitSchema,
    lab: LabUnitSchema,
    build: BuildUnitSchema,
};
//# sourceMappingURL=schemas.js.map